import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Receipt as ClaimIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  ShoppingCart as ItemIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import ListFilterBar from '../../common/ListFilterBar';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { CLAIM_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmRejectDialog from '../../common/ConfirmRejectDialog';
import { SUCCESS } from '../../common/SaaSTokens';
import { canEditClaim, canRejectClaim } from '../../../utils/financialPermissions';
import {
  parseClaimLineItems,
  claimLineItemsPrimaryLabel,
  claimLineItemsSummaryLabel,
  formatClaimLineItemAmount,
} from '../../../utils/claimLineItems';

const NEON = '#38BDF8';

const ClaimItemBoughtCell = ({ claim }) => {
  const items = parseClaimLineItems(claim);

  if (!items.length) {
    return (
      <Typography variant="body2" sx={{ color: alpha('#fff', 0.45), fontStyle: 'italic' }}>
        —
      </Typography>
    );
  }

  const primary = items[0].name;
  const extraCount = items.length - 1;

  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.25, maxWidth: 360 }}>
      {items.map((item, index) => {
        const amountLabel = formatClaimLineItemAmount(item.amount);
        return (
          <Box key={`${item.name}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Typography
              component="span"
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.68rem',
                color: alpha(NEON, 0.85),
                minWidth: 18,
                flexShrink: 0,
              }}
            >
              {index + 1}.
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', lineHeight: 1.45 }}>
                {item.name}
              </Typography>
              {amountLabel && (
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: alpha('#fff', 0.65), mt: 0.25 }}>
                  {amountLabel}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top-start" enterDelay={300}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: 320 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {primary}
        </Typography>
        {extraCount > 0 && (
          <Chip
            label={`+${extraCount}`}
            size="small"
            sx={{
              height: 22,
              flexShrink: 0,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: alpha(NEON, 0.12),
              color: NEON,
              border: `1px solid ${alpha(NEON, 0.28)}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const ClaimList = () => {
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClaims, setTotalClaims] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      searchable: true,
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'on_hold', label: 'On hold' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
      ],
    },
    {
      name: 'is_paid',
      label: 'Payment',
      type: 'select',
      options: [
        { value: '1', label: 'Paid' },
        { value: '0', label: 'Unpaid' },
      ],
    },
  ];

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: rowsPerPage };
      if (filters.status) params.status = filters.status;
      if (filters.is_paid !== undefined && filters.is_paid !== '') params.is_paid = filters.is_paid;
      const response = await axiosInstance.get('/claims', { params });
      if (response.data.data) {
        setClaims(response.data.data);
        setTotalClaims(response.data.total);
      } else {
        setClaims(Array.isArray(response.data) ? response.data : []);
        setTotalClaims(Array.isArray(response.data) ? response.data.length : 0);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Failed to fetch claims');
      setClaims([]);
      setTotalClaims(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchClaims();
  }, [user, currentPage, rowsPerPage, filters, location.pathname, location.state?.refreshList]);

  const handleRejectClaim = async (reason) => {
    if (!rejectTarget) return;
    try {
      setRejectLoading(true);
      setRejectError(null);
      await axiosInstance.put(`/claims/${rejectTarget.id}/reject`, {
        rejection_reason: reason,
      });
      setRejectTarget(null);
      setSuccess(`Claim "${claimLineItemsSummaryLabel(rejectTarget) || rejectTarget.description || 'Claim'}" has been rejected successfully!`);
      await fetchClaims();
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Failed to reject claim.';
      setRejectError(msg);
      throw err;
    } finally {
      setRejectLoading(false);
    }
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'pending', 'approved'];
    if (index >= filterMap.length) return;

    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
      delete nextFilters.is_paid;
    } else {
      nextFilters.status = nextFilter;
      delete nextFilters.is_paid;
    }
    setFilters(nextFilters);
    setPage(0);
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'warning',
      on_hold: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colorMap[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      pending: <PendingIcon fontSize="small" />,
      on_hold: <PendingIcon fontSize="small" />,
      approved: <ApprovedIcon fontSize="small" />,
      rejected: <RejectedIcon fontSize="small" />,
    };
    return iconMap[status] || null;
  };

  const filteredClaims = claims.filter((c) => {
    const matchesSearch =
      !searchTerm ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const totalAmount = claims.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

  const statsData = [
    {
      icon: ClaimIcon,
      value: totalClaims,
      label: 'Total Claims',
      color: 'info',
    },
    {
      icon: PendingIcon,
      value: claims.filter((c) => c.status === 'pending').length,
      label: 'Pending',
      color: 'warning',
    },
    {
      icon: ApprovedIcon,
      value: claims.filter((c) => c.status === 'approved').length,
      label: 'Approved',
      color: 'success',
    },
    {
      icon: MoneyIcon,
      value: `RM ${totalAmount.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: 'Total Amount',
      color: 'primary',
      valueSize: 'xsmall',
    },
  ];

  const columns = [
    {
      key: 'item_bought',
      label: 'Item Bought',
      icon: ItemIcon,
      minWidth: 220,
      sortAccessor: (c) => claimLineItemsPrimaryLabel(c).toLowerCase(),
      render: (c) => <ClaimItemBoughtCell claim={c} />,
    },
    {
      key: 'description',
      label: 'Description',
      icon: DescriptionIcon,
      minWidth: 200,
      render: (c) => (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: alpha(theme.palette.common.white, 0.95),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {c.description}
        </Typography>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      icon: MoneyIcon,
      minWidth: 120,
      render: (c) => (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.success.main,
          }}
        >
          RM {parseFloat(c.amount).toFixed(2)}
        </Typography>
      ),
    },
    {
      key: 'user.name',
      label: 'Claimant',
      icon: PersonIcon,
      minWidth: 160,
      render: (c) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.875rem',
            }}
          >
            {c.user?.name?.charAt(0)?.toUpperCase() ||
              c.created_by?.charAt(0)?.toUpperCase() ||
              'U'}
          </Avatar>
          <Typography variant="body2">
            {c.user?.name || c.created_by || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      minWidth: 140,
      render: (c) => (
        <Chip
          icon={getStatusIcon(c.status)}
          label={c.status.toUpperCase()}
          color={getStatusColor(c.status)}
          size="small"
          sx={{ fontWeight: 500, boxShadow: 1 }}
        />
      ),
    },
    {
      key: 'is_paid',
      label: 'Paid',
      align: 'center',
      minWidth: 110,
      render: (c) => (
        <Chip
          icon={c.is_paid ? <PaidIcon fontSize="small" /> : undefined}
          label={c.is_paid ? 'PAID' : 'UNPAID'}
          color={c.is_paid ? 'success' : undefined}
          size="small"
          variant={c.is_paid ? 'filled' : 'outlined'}
          sx={{
            fontWeight: 600,
            minWidth: 88,
            ...(c.is_paid
              ? {}
              : {
                  color: '#FDE68A',
                  borderColor: alpha('#FBBF24', 0.72),
                  backgroundColor: alpha('#F59E0B', 0.18),
                  '& .MuiChip-label': { color: '#FDE68A', fontWeight: 700 },
                }),
          }}
        />
      ),
    },
    {
      key: 'submitted_at',
      label: 'Submitted',
      icon: CalendarIcon,
      minWidth: 140,
      render: (c) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {format(new Date(c.created_at), 'MMM dd, yyyy')}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 180,
      render: (c) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Claim" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/claims/${c.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEditClaim(user, c) && (
            <Tooltip title="Edit Claim" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/claims/edit/${c.id}`, { state: { claim: c } });
                }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canRejectClaim(user, c) && (
            <Tooltip title="Reject Claim" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejectError(null);
                  setRejectTarget(c);
                }}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.12),
                  },
                }}
              >
                <RejectedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a',0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
        />
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <Loading message="Loading claims list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Claims"
        subtitle="Track and manage employee claims and reimbursements"
        icon={ClaimIcon}
        actionButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/claims/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.22)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.34)',
              },
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
            }}
          >
            Add Claim
          </Button>
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a',0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      {/* Stats Cards */}
      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={
          activeStatFilter === 'pending'
            ? 1
            : activeStatFilter === 'approved'
            ? 2
            : 0
        }
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPage(0);
          setCurrentPage(1);
        }}
        onClearFilters={() => {
          setFilters({});
          setSearchTerm('');
          setPage(0);
          setCurrentPage(1);
          setActiveStatFilter('all');
        }}
        filterOptions={filterOptions}
        searchPlaceholder="Search by description or claimant..."
      />

      {/* Claims Table */}
      <ListBulkToolbar
        config={CLAIM_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: CLAIM_BULK_CONFIG.endpoint,
          rejectEndpoint: CLAIM_BULK_CONFIG.bulkRejectEndpoint,
          setItems: setClaims,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'claim(s)',
          refetch: fetchClaims,
          setError,
          filterRejectIds: (ids) => ids.filter((id) => {
            const claim = claims.find((row) => Number(row.id) === Number(id));
            return claim?.status === 'pending';
          }),
        })}
      />


      <TableList
        columns={columns}
        data={filteredClaims}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => {
          setPage(newPage);
          setCurrentPage(newPage + 1);
        }}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
          setCurrentPage(1);
        }}
        {...bulkTableProps(CLAIM_BULK_CONFIG, user, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <ClaimIcon />,
          title: 'No claims found',
          description:
            searchTerm || filters.status !== 'all'
              ? 'Try adjusting your filters to see more claims.'
              : 'Get started by creating your first claim.',
        }}
        onRowClick={(c) => navigate(`/claims/${c.id}`)}
      />

      <ConfirmRejectDialog
        open={Boolean(rejectTarget)}
        onClose={() => !rejectLoading && setRejectTarget(null)}
        onConfirm={handleRejectClaim}
        title="Reject claim"
        itemName={rejectTarget ? (claimLineItemsSummaryLabel(rejectTarget) || rejectTarget.description || '') : ''}
        confirmLabel="Reject claim"
        loading={rejectLoading}
        errorMessage={rejectError}
        count={1}
      />
    </Container>
  );
};

export default ClaimList;